import { IconCheck, IconArrowLeft, IconArrowRight } from "@tabler/icons-react";
import { formatValue } from "@/domain/calibration-params";
import type { NvpParameter } from "@/domain/calibration-params";

interface ParamDiffTableProps {
  diffs: Array<{ name: string; expected_value: unknown; live_value: unknown; accuracy: number; chp_data_type?: string }>;
  expectedHeader: string;
  errors?: string[];
  /** When a write action is hovered, highlight the destination column and point
   * an arrow at it: "live" = restore (source→live), "source" = accept (live→source). */
  writeTarget?: "source" | "live" | null;
  /** Event the golden was measured at, shown under the source column. On
   * accept-hover ("source") it swaps to currentEventName to preview that the
   * live reading becomes the new golden for the current event. Wing cals only. */
  sourceEventName?: string | null;
  currentEventName?: string | null;
}

export function ParamDiffTable({
  diffs,
  expectedHeader,
  errors,
  writeTarget,
  sourceEventName,
  currentEventName,
}: ParamDiffTableProps) {
  const showProvenance = sourceEventName != null || currentEventName != null;
  // Accept previews the live reading becoming the new golden → its provenance
  // flips to the current event. Resting/restore keep the golden's own event.
  const provenanceEvent =
    writeTarget === "source" ? (currentEventName ?? "—") : (sourceEventName ?? "—");
  return (
    <div className="space-y-3">
      {errors && errors.length > 0 && (
        <div className="text-sm text-destructive">
          Errors: {errors.join(", ")}
        </div>
      )}
      {diffs.length === 0 ? (
        <div className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
          <IconCheck className="h-4 w-4" />
          All parameters match {expectedHeader.toLowerCase()} values
        </div>
      ) : (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-muted-foreground/70 text-[11px] uppercase tracking-wider">
              <th className="text-left font-medium pb-1.5">Parameter</th>
              <th className="text-right font-medium pb-1.5">
                <span
                  className={
                    writeTarget === "source" ? "text-green-700 dark:text-green-400 font-semibold" : undefined
                  }
                >
                  {expectedHeader}
                </span>
              </th>
              <th className="text-right font-medium pb-1.5">
                <span
                  className={writeTarget === "live" ? "text-red-600 dark:text-red-400 font-semibold" : undefined}
                >
                  Live
                </span>
              </th>
            </tr>
            {showProvenance && (
              <tr className="text-[10px] tracking-wide">
                <th />
                <th className="text-right font-normal pb-1.5">
                  <span className="inline-flex items-center justify-end gap-1">
                    <span
                      className={
                        writeTarget === "source"
                          ? "text-green-700 dark:text-green-400 font-medium"
                          : "text-muted-foreground/70"
                      }
                    >
                      {provenanceEvent}
                    </span>
                    {writeTarget === "live" && (
                      <IconArrowRight className="h-3.5 w-3.5 text-red-500" />
                    )}
                    {writeTarget === "source" && (
                      <IconArrowLeft className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                    )}
                  </span>
                </th>
                <th />
              </tr>
            )}
          </thead>
          <tbody>
            {diffs.map((d) => (
              <tr key={d.name} className="border-t border-border/50">
                <td className="py-1.5 font-mono text-foreground/80">{d.name}</td>
                <td className="py-1.5 text-right font-mono text-green-700 dark:text-green-400">
                  {formatValue(d.expected_value as NvpParameter["Value"], d.accuracy, d.chp_data_type)}
                </td>
                <td className="py-1.5 text-right font-mono text-red-600 dark:text-red-400">
                  {formatValue(d.live_value as NvpParameter["Value"], d.accuracy, d.chp_data_type)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
